import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Regular14TransparentBlack600, Regular18TransparentBlack900 } from '@app/components/Text';
import MorteeMode from '@mortee/morteeMode';
import validationSystemServices from '@mortee/services/validationSystemServices';
import EditValidationRecordForm from '@mortee/routes/validationSystem/editValidtionRecord/EditValidationRecordForm';
import useLoadable from '@app/hooks/loadable/useLoadable';
import {
  NULL_ORG,
  SupplierValidationRecord,
  SupplierValidationRecordStatusSubsets,
  transformSupplierValidationRecord,
} from '@mortee/domain/validationSystem';
import useInfraStores from '@app/hooks/useInfraStores';
import Loader from '@app/components/Loader';
import { LoadableCreator } from '@app/utils/Loadable';
import { showYesNoModalAsync } from '@app/components/Modal';
import CopyableValue from '@app/components/CopyableValue';
import { isDefined } from '@app/utils/utils';
import { MainTabs } from '@mortee/routes/validationSystem/editValidtionRecord/mainTabs/EditValidationRecordMainTabs';
import { SecondaryTabs } from '@mortee/routes/validationSystem/editValidtionRecord/secondaryTabs/EditValidationRecordSecondaryTabs';
import SeparatorLine from '@app/components/SeparatorLine';
import PageWithHeader from '@app/components/PageWithHeader';
import PageHeader from '@app/components/header/PageHeader';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import InlinePreviousAndNextArrows from '@app/components/InlinePreviousAndNextArrows';
import { ButtonProps } from '@app/components/Button';

type EnumValue<T> = T[keyof T];

interface RouteParams {
  staticId: string;
}

const INITIAL_MAIN_TAB = MainTabs.generalInfo;

const EditValidationRecordPage: FC = observer(() => {
  const { userStore, navigationStore } = useInfraStores<MorteeMode>();
  const history = useHistory();
  const location = useLocation();
  const { staticId } = useParams<RouteParams>();

  const [selectedMainTab, setSelectedMainTab] = useState<EnumValue<typeof MainTabs> | null>(null);
  const [selectedSecondaryTab, setSelectedSecondaryTab] = useState<EnumValue<typeof SecondaryTabs>>(SecondaryTabs.comments);

  const isAnyFormSavedRef = useRef<boolean>(false);
  const isCurrentFormUnsaved = useRef<boolean>(false);

  // Get previous and next record IDs from location state or query params
  const locationState = location.state as { previousRecordId?: string; nextRecordId?: string } | undefined;
  const queryParams = new URLSearchParams(location.search);

  const [adjacentRecordsLoadable] = useLoadable(async () => {
    if (!staticId) {
      return { previousId: undefined, nextId: undefined };
    }

    // Fetch a page of records to find adjacent ones
    try {
      const pageSize = 100;
      const serverResponse = await validationSystemServices.getValidationRecords(1, pageSize, undefined, undefined);

      const records = serverResponse.content.map((record) => transformSupplierValidationRecord(record, userStore.user?.id));
      const currentIndex = records.findIndex((record) => record.staticId === staticId);

      if (currentIndex === -1) {
        return { previousId: undefined, nextId: undefined };
      }

      return {
        previousId: currentIndex > 0 ? records[currentIndex - 1]?.staticId : undefined,
        nextId: currentIndex < records.length - 1 ? records[currentIndex + 1]?.staticId : undefined,
      };
    } catch (error) {
      return { previousId: undefined, nextId: undefined };
    }
  }, [staticId, userStore.user?.id]);

  // Only use adjacentRecordsLoadable result if it's resolved, otherwise use location state or query params
  const previousRecordId =
    locationState?.previousRecordId ||
    queryParams.get('previousRecordId') ||
    (adjacentRecordsLoadable.isResolved() ? adjacentRecordsLoadable.result?.previousId : undefined) ||
    undefined;
  const nextRecordId =
    locationState?.nextRecordId ||
    queryParams.get('nextRecordId') ||
    (adjacentRecordsLoadable.isResolved() ? adjacentRecordsLoadable.result?.nextId : undefined) ||
    undefined;

  const currentStaticIdRef = useRef<string>(staticId || '');
  // We need the static id as ref because the static id from the params does not update to the latest value (due to closure)
  currentStaticIdRef.current = staticId || '';

  const [recordLoadable, reloadRecord, setRecordLoadable] = useLoadable(async () => {
    if (!staticId) {
      throw new Error('Static ID is required');
    }
    const serverResponse = await validationSystemServices.getValidationRecordByStaticId(staticId);
    return transformSupplierValidationRecord(serverResponse, userStore.user?.id);
  }, [staticId]);

  async function onChangeMainTab(newTab: MainTabs): Promise<void> {
    if (await checkCanChangeTab()) {
      // We left the tab so there is no unsaved form data
      isCurrentFormUnsaved.current = false;
      setSelectedMainTab(newTab);
    }
  }

  async function checkCanChangeTab(): Promise<boolean> {
    if (isCurrentFormUnsaved.current) {
      return await showYesNoModalAsync('This tab has unsaved changes', `Leave editing tab`, 'Leave Without Saving', 'Stay');
    }

    return true;
  }

  async function checkCanNavigate(): Promise<boolean> {
    if (isCurrentFormUnsaved.current) {
      return await showYesNoModalAsync(
        'This form has unsaved changes',
        `Leave editing record ${recordLoadable.result?.presentationId}?`,
        'Leave Without Saving',
        'Stay',
      );
    }

    return true;
  }

  async function onPreviousRecordClick(): Promise<void> {
    if (!previousRecordId) {
      return;
    }

    const editLink = navigationStore.generateEditValidationRecordLink(previousRecordId);
    const navigationState = {
      previousRecordId: undefined, // Will be fetched on the new page
      nextRecordId: staticId, // Current record becomes next for the previous one
    };

    if (typeof editLink === 'string') {
      history.push(editLink, navigationState);
    } else if (editLink && editLink.pathname) {
      history.push({ ...editLink, state: navigationState });
    }
  }

  async function onNextRecordClick(): Promise<void> {
    if (!nextRecordId) {
      return;
    }

    const editLink = navigationStore.generateEditValidationRecordLink(nextRecordId);
    const navigationState = {
      previousRecordId: staticId, // Current record becomes previous for the next one
      nextRecordId: undefined, // Will be fetched on the new page
    };

    if (typeof editLink === 'string') {
      history.push(editLink, navigationState);
    } else if (editLink && editLink.pathname) {
      history.push({ ...editLink, state: navigationState });
    }
  }

  function wrapMoveClickWithQuestionBefore(moveAction: ButtonProps['onClick'] | undefined): ButtonProps['onClick'] | undefined {
    if (moveAction) {
      return async (...args) => {
        if (await checkCanNavigate()) {
          moveAction(...args);
        }
      };
    }
  }

  function onSaved(
    updatedRecordStaticId: string,
    updatedRecord: SupplierValidationRecord | undefined,
    userWantsToClose: boolean,
  ): void {
    isAnyFormSavedRef.current = true;

    if (updatedRecordStaticId !== currentStaticIdRef.current) {
      return;
    }

    if (userWantsToClose) {
      history.push(navigationStore.generateSupplierValidationRecordsTablePageLink()); // Go to the Validation System page
      return;
    }

    if (updatedRecord) {
      setRecordLoadable(LoadableCreator.resolved(updatedRecord));
    } else {
      reloadRecord.silent();
    }
  }

  function onRecordRefreshRequested(): void {
    reloadRecord.silent();
  }

  useEffect(() => {
    if (recordLoadable.isResolved() && selectedMainTab === null) {
      setSelectedMainTab(getMainTabByRecordData(recordLoadable.result));
    }
  }, [recordLoadable, selectedMainTab]);

  function getMainTabByRecordData(record: SupplierValidationRecord): MainTabs {
    if (SupplierValidationRecordStatusSubsets.shouldOpenEditModalOnVerificationTab.includes(record.status.value)) {
      return MainTabs.verification;
    }

    return INITIAL_MAIN_TAB;
  }

  const headerContent = (
    <HeaderLayout>
      <LeftSection>
        {recordLoadable.resolve(
          (record) => (
            <CopyableValue dataTestId='presentationId-copyable' label='VR' value={record.presentationId}>
              <RecordIdText>{record.presentationId}</RecordIdText>
            </CopyableValue>
          ),
          () => (
            <RecordIdText>Loading...</RecordIdText>
          ),
        )}
      </LeftSection>
      <CenterSection>
        {recordLoadable.resolve(
          (record) => (
            <>
              <MainTitle>
                <CopyableValue dataTestId='supplierName-copyable' label='Supplier name' value={record.supplierName}>
                  {record.supplierName}
                </CopyableValue>
              </MainTitle>
              <Subtitle>
                Referring customer -{' '}
                {record.organization?.name ? (
                  <CopyableValue dataTestId='customerName-copyable' label='Customer name' value={record.organization?.name} />
                ) : (
                  NULL_ORG.name
                )}
              </Subtitle>
            </>
          ),
          () => (
            <>
              <MainTitle>Loading...</MainTitle>
              <Subtitle>Loading...</Subtitle>
            </>
          ),
        )}
      </CenterSection>

      <RightSection>
        <RightSectionContent>
          <CloseButton
            data-testid='btn-modal-close'
            onClick={async (): Promise<void> => {
              if (await checkCanNavigate()) {
                onSaved(staticId, undefined, true);
              }
            }}
            aria-label='Close'
          >
            ✕
          </CloseButton>
          <StyledPreviousAndNextArrows
            onPreviousClick={wrapMoveClickWithQuestionBefore(previousRecordId ? onPreviousRecordClick : undefined)}
            previousItemText='Previous Record'
            onNextClick={wrapMoveClickWithQuestionBefore(nextRecordId ? onNextRecordClick : undefined)}
            nextItemText='Next Record'
            disableButtons={!recordLoadable.isResolved()}
          />
        </RightSectionContent>
      </RightSection>
    </HeaderLayout>
  );

  if (!staticId) {
    return (
      <PageWithHeader width='fullWithoutPadding' paddingTop='none' header={<PageHeader hideTitle sideContent={headerContent} />}>
        <div>Error: Static ID is required</div>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader width='fullWithoutPadding' paddingTop='none' header={<PageHeader hideTitle sideContent={headerContent} />}>
      <div data-testid='edit-validation-record-page'>
        <PageContainer>
          <SeparatorLine />
          {recordLoadable.resolve(
            (record) => (
              <ExpandedEditValidationRecordForm
                key={record.staticId}
                record={record}
                selectedMainTab={{ value: selectedMainTab ?? INITIAL_MAIN_TAB, onChange: onChangeMainTab }}
                selectedSecondaryTab={{ value: selectedSecondaryTab, onChange: setSelectedSecondaryTab }}
                onSaved={(updatedRecord, userWantsToClose): void => onSaved(staticId, updatedRecord, userWantsToClose)}
                onRecordRefreshRequested={onRecordRefreshRequested}
                onIsFormUnsavedChanged={(isFormUnsaved): void => {
                  if (isDefined(isFormUnsaved)) {
                    isCurrentFormUnsaved.current = isFormUnsaved;
                  }
                }}
              />
            ),
            () => (
              <Loader spinning transparent />
            ),
          )}
        </PageContainer>
      </div>
    </PageWithHeader>
  );
});

export default EditValidationRecordPage;

const PageContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  position: relative;
  box-shadow: var(--box-shadow-3);
  padding: 0;
  background: white;
  border-radius: 8px;

  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);

  --side-padding: 24px;
`;

const ExpandedEditValidationRecordForm = styled(EditValidationRecordForm)`
  flex: 1;
  overflow-y: auto;
`;

const HeaderLayout = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-height: 80px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const CenterSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const RightSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: rgba(28, 32, 36, 0.6);
  padding: 4px 6px;
  border-radius: 4px;
  transition:
    color 0.15s,
    background 0.15s;
  align-self: flex-end;

  &:hover {
    color: #1c2024;
    background: rgba(28, 32, 36, 0.07);
  }
`;

const MainTitle = styled(Regular18TransparentBlack900.div)`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Subtitle = styled(Regular14TransparentBlack600.div)`
  font-size: 14px;
  color: #666;
`;

const StyledPreviousAndNextArrows = styled(InlinePreviousAndNextArrows)`
  align-self: flex-start;
  flex: 0 0 auto;
  justify-content: flex-end;
  align-items: baseline;
`;

const RecordIdText = styled.span`
  font-size: 14px;
  color: var(--transparent-black-900);
`;
